import { useQuery } from '@apollo/client/react'
import { Award, CheckCircle2, GraduationCap, Loader, PlayCircle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { BrandLogo } from '~/components/BrandLogo'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Card, CardContent } from '~/components/ui/card'
import { Progress } from '~/components/ui/progress'
import { GQL_MY_SESSIONS } from '~/gql/sessions/sessions'
import { useBranding } from '~/helpers/branding/branding'
import { formatSessionDate } from '~/helpers/dates/session-dates'
import { env } from '~/helpers/env'
import { useAuth } from '~/helpers/store/auth'
import { cn } from '~/lib/utils'
import LearnerUserMenu from '~/pages/components/LearnerUserMenu'
import PathSteps from '~/pages/scorm/components/PathSteps'

type CompletionStatus = 'COMPLETED' | 'INCOMPLETE' | 'NOT_ATTEMPTED'

type BadgeVariant = 'success' | 'warning' | 'secondary'

/** Config visuelle par état : liseré de carte, variante de badge, libellés. */
function useStatusMeta() {
  const { t } = useTranslation()
  return (status: CompletionStatus): {
    stripe: string
    badgeVariant: BadgeVariant
    badgeLabel: string
    cta: string
  } => {
    switch (status) {
      case 'COMPLETED':
        return { stripe: 'border-l-green-500', badgeVariant: 'success', badgeLabel: t('status.completed'), cta: t('home.review') }
      case 'INCOMPLETE':
        return { stripe: 'border-l-yellow-500', badgeVariant: 'warning', badgeLabel: t('home.inProgress'), cta: t('home.resume') }
      default:
        return { stripe: 'border-l-border', badgeVariant: 'secondary', badgeLabel: t('status.not_attempted'), cta: t('sessions.my.start') }
    }
  }
}

/** Ces dates-ci portent l'année : elles peuvent remonter à plusieurs mois. */
const WITH_YEAR: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' }

function MySessions() {
  const { t, i18n } = useTranslation()
  const { timezone } = useBranding()
  const navigate = useNavigate()
  const { payload } = useAuth()
  const statusMeta = useStatusMeta()
  const { data, loading } = useQuery(GQL_MY_SESSIONS, { fetchPolicy: 'cache-and-network' })
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  const sessions = data?.mySessions ?? []
  // Un parcours ne compte comme terminé que si TOUS ses modules le sont : le
  // bandeau du haut dit la même chose que chaque carte, pas autre chose.
  const completedCount = sessions.filter(s => s.progress?.completed === true).length
  const overallPct = sessions.length ? Math.round((completedCount / sessions.length) * 100) : 0

  const downloadCertificate = async (sessionId: string) => {
    setDownloadingId(sessionId)
    try {
      const res = await fetch(`${env.api.url}/certificates/session/${sessionId}`, {
        credentials: 'include',
      })
      if (!res.ok) throw new Error(`certificate download failed (${res.status})`)
      const blob = await res.blob()
      const fileName = res.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1]
        ?? 'attestation.pdf'
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error(t('home.certificate.error'))
    } finally {
      setDownloadingId(null)
    }
  }

  return (
    <div className="min-h-dvh bg-background">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background/80 px-6 py-4 backdrop-blur">
        <BrandLogo className="h-8 w-auto" fallbackClassName="text-lg" />
        <LearnerUserMenu variant="light" />
      </header>

      <main className="mx-auto w-full max-w-5xl p-6">
        <div className="mb-8">
          <h1 className="font-heading text-2xl tracking-tight text-balance">
            {t('home.title')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('home.subtitle', { name: payload?.firstName ?? '' })}
          </p>

          {sessions.length > 0 && (
            <div className="mt-5 flex items-center gap-3">
              <Progress value={overallPct} className="h-2 max-w-xs" />
              <span className="whitespace-nowrap text-sm font-medium tabular-nums text-muted-foreground">
                {t('home.summary', { count: completedCount, total: sessions.length })}
              </span>
            </div>
          )}
        </div>

        {loading && sessions.length === 0
          ? (
              <div className="flex justify-center p-16">
                <Loader className="size-6 animate-spin text-muted-foreground" />
              </div>
            )
          : sessions.length === 0
            ? (
                <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border p-12 text-center">
                  <GraduationCap className="size-10 text-muted-foreground" />
                  <p className="font-medium">{t('sessions.my.empty')}</p>
                  <p className="max-w-sm text-sm text-muted-foreground">{t('home.empty.hint')}</p>
                </div>
              )
            : (
                <div className="grid gap-4 sm:grid-cols-2">
                  {sessions.map((s) => {
                    const steps = s.modules ?? []
                    const isPath = steps.length > 1
                    // L'avancement vient du PARCOURS, plus d'une ligne de suivi :
                    // un parcours de cinq modules dont un est fini n'est pas
                    // « terminé ». `progress` est calculé côté API, seul endroit
                    // qui décide ce que « terminé » veut dire.
                    const done = s.progress?.completedModules ?? 0
                    const total = s.progress?.totalModules ?? steps.length
                    const completed = s.progress?.completed === true
                    const status: CompletionStatus = completed
                      ? 'COMPLETED'
                      : done > 0 || steps.some(m => m.myTracking != null)
                        ? 'INCOMPLETE'
                        : 'NOT_ATTEMPTED'
                    const meta = statusMeta(status)

                    // Sur un parcours à un module, on garde la carte compacte
                    // d'avant : une liste d'étapes d'un seul élément serait une
                    // cérémonie pour rien.
                    const single = steps[0]
                    const singleLaunchable = single?.unlocked === true && single.module?.status === 'READY'

                    return (
                      <Card
                        key={s.id}
                        className={cn(
                          'flex flex-col border-l-4 transition-shadow hover:shadow-md',
                          meta.stripe,
                        )}
                      >
                        <CardContent className="flex flex-1 flex-col gap-3 p-5">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h2 className="truncate font-heading text-lg leading-tight">{s.name}</h2>
                              <p className="mt-0.5 truncate text-sm text-muted-foreground">
                                {isPath
                                  ? t('home.path.summary', { done, total })
                                  : `${single?.module?.title ?? ''}${single?.module?.scormVersion ? ` · SCORM ${single.module.scormVersion}` : ''}`}
                              </p>
                            </div>
                            <Badge variant={meta.badgeVariant} className="shrink-0">{meta.badgeLabel}</Badge>
                          </div>

                          {isPath && total > 0 && (
                            <Progress value={Math.round((done / total) * 100)} className="h-1.5" />
                          )}

                          {isPath
                            ? <PathSteps steps={steps} onLaunch={mid => navigate(`/scorm/${s.id}/${mid}`)} />
                            : (
                                <>
                                  {(single?.myTracking?.scoreRaw != null
                                    || (completed && single?.myTracking?.completedAt != null)) && (
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                      {single?.myTracking?.scoreRaw != null && (
                                        <span>
                                          {t('common.score')}
                                          {' : '}
                                          <span className="font-medium tabular-nums text-foreground">
                                            {single.myTracking.scoreRaw}
                                            {single.myTracking.scoreMax != null ? ` / ${single.myTracking.scoreMax}` : ''}
                                          </span>
                                        </span>
                                      )}
                                      {completed && single?.myTracking?.completedAt != null && (
                                        <span className="inline-flex items-center gap-1">
                                          <CheckCircle2 className="size-3.5 text-green-500" />
                                          {t('home.completedOn')}
                                          {' '}
                                          {formatSessionDate(single.myTracking.completedAt, i18n.language, timezone, WITH_YEAR)}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  {single?.unlocked === false && (
                                    <p className="text-sm text-muted-foreground">
                                      {single.lockReason === 'SCHEDULED' && single.unlockAt
                                        ? t('home.path.opensOn', { date: formatSessionDate(single.unlockAt, i18n.language, timezone, WITH_YEAR) })
                                        : t('home.path.awaitingPrevious')}
                                    </p>
                                  )}
                                </>
                              )}

                          <div className="mt-auto flex items-center gap-2 pt-1">
                            {!isPath && (
                              <Button
                                className="flex-1"
                                onClick={() => navigate(`/scorm/${s.id}/${single!.module!.id}`)}
                                disabled={!singleLaunchable}
                              >
                                <PlayCircle className="mr-2 size-4" />
                                {meta.cta}
                              </Button>
                            )}
                            {completed && (
                              <Button
                                variant="outline"
                                className={cn(isPath && 'flex-1')}
                                onClick={() => downloadCertificate(s.id)}
                                disabled={downloadingId === s.id}
                                aria-label={t('home.certificate.download')}
                              >
                                {downloadingId === s.id
                                  ? <Loader className="size-4 animate-spin" />
                                  : <Award className="size-4" />}
                                <span className="ml-2 hidden sm:inline">{t('home.certificate.download')}</span>
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              )}
      </main>
    </div>
  )
}

export default MySessions
